import React, { useEffect, useRef, useState } from "react";
import { Box, Container, Grid, Typography } from "@mui/material";

// Allowed animation types
type AnimationType = "fade-up" | "zoom-in";

// Animated component that applies inline styles based on the animation type
type AnimatedProps = {
  animation: AnimationType;
  delay?: number;
  children: React.ReactNode;
};

function Animated({ animation, delay = 0, children }: AnimatedProps) {
  // Custom hook to detect when an element is in view (with an optional delay)
  function useInView(delay: number = 0) {
    const ref = useRef<HTMLDivElement | null>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
      const el = ref.current;
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisible(true);
            }, delay);
            observer.unobserve(el);
          }
        },
        { threshold: 0.1 }
      );

      observer.observe(el);

      return () => {
        observer.disconnect();
      };
    }, [delay]);

    return [ref, visible] as const;
  }
  const [ref, visible] = useInView(delay);

  // Base transition styles
  const baseTransition = "opacity 0.8s ease-out, transform 0.8s ease-out";

  // Use React.CSSProperties to include transform property
  let initialStyle: React.CSSProperties = {
    opacity: 0,
    transition: baseTransition,
  };
  if (animation === "fade-up") {
    initialStyle = { ...initialStyle, transform: "translateY(20px)" };
  } else if (animation === "zoom-in") {
    initialStyle = { ...initialStyle, transform: "scale(0.9)" };
  }

  const visibleStyle: React.CSSProperties = {
    opacity: 1,
    transform: "none",
    transition: baseTransition,
  };

  return (
    <Box ref={ref} sx={visible ? visibleStyle : initialStyle}>
      {children}
    </Box>
  );
}
export default function AboutUs() {
  return (
    <Box>
      {/* Main About Us Section */}
      <Box sx={{ backgroundColor: "#f3f4f6", py: 8, textAlign: "center" }}>
        <Container maxWidth="md">
          <Animated animation="fade-up">
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{ fontWeight: 800 }}
            >
              About Us
            </Typography>
            <Typography variant="subtitle1" component="p">
              We are a passionate team of innovators dedicated to transforming
              the fitness industry. Our mission is to make fitness accessible to
              everyone. We believe that fitness should be a right, not a
              privilage. That is why our free tier has such extensive features.
              We believe that everyone should have access to the tools they need
              to live a healthy life. For those looking to supercharge their
              progress, the pro tier offers great toold to supercharge your
              fitness.
            </Typography>
          </Animated>
        </Container>
      </Box>

      {/* Cofounder 1 Section: Left Image, Right Text */}
      <Container sx={{ py: 8 }}>
        <Grid container spacing={4} alignItems="center" sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Animated animation="fade-up">
              <Box
                component="img"
                src="https://media.licdn.com/dms/image/v2/D5603AQEDya8nMziKcQ/profile-displayphoto-shrink_200_200/profile-displayphoto-shrink_200_200/0/1697836994498?e=1745452800&v=beta&t=wjEQeyjO6hhputV2FAQnY4q-wq50cXJNNi8l3KMfHog"
                alt="Cofounder One"
                sx={{ width: 200, borderRadius: 2 }}
              />
            </Animated>
          </Grid>
          <Grid item xs={12} md={6}>
            <Animated animation="fade-up" delay={200}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                Olimjon Nematov
              </Typography>
              <Typography variant="body1">
                Olimjon is the founder, CTO and CEO. He is a software engineer
                with a passion for fitness and wellness. He is the driving force
                behind our mission to make fitness accessible to everyone. With
                a background in technology and design, they drive the
                development force of our team.
              </Typography>
            </Animated>
          </Grid>
        </Grid>
      </Container>

      {/* Cofounder 2 Section: Right Image, Left Text */}
      <Container
        sx={{
          py: 8,
          display: "flex",
          flexDirection: "space-between",
          backgroundColor: "#f3f4f6",
          borderRadius: 8,
        }}
      >
        <Grid container spacing={4} alignItems="center" sx={{ mb: 8 }}>
          {/* Text Column */}
          <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
            <Animated animation="fade-up" delay={200}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                Nickie Patel
              </Typography>
              <Typography variant="body1">
                Nickie is the CMO and CSA (Chief scientific advisor). With a
                bachelors in Health and Exercise science, Health and Wellness
                and a masters in dietetics and nutritional sceience at Florida
                International University, Nickie is the health expert of the
                team. Nickie's expertise in health and wellness is the core
                behind our proprietary algorithms that power our products.
              </Typography>
            </Animated>
          </Grid>
          {/* Image Column */}
          <Grid
            item
            xs={12}
            md={6}
            order={{
              xs: 1,
              md: 2,
            }}
            sx={{ textAlign: "right" }}
          >
            <Animated animation="fade-up">
              <Box
                component="img"
                src="https://media.licdn.com/dms/image/v2/C5603AQF7q72NRWhM_w/profile-displayphoto-shrink_200_200/profile-displayphoto-shrink_200_200/0/1653692288847?e=1745452800&v=beta&t=BECD3r5-klKTw9W3Pe69KF5xLIQJDx5GWLoMr_f5jAU"
                alt="Cofounder Two"
                sx={{ width: 200, borderRadius: 2 }}
              />
            </Animated>
          </Grid>
        </Grid>
      </Container>

      {/* Cofounder 3 Section: Left Image, Right Text */}
      <Container sx={{ py: 8 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Animated animation="fade-up">
              <Box
                component="img"
                src="https://media.licdn.com/dms/image/v2/D5603AQHh2GXTPC41ng/profile-displayphoto-shrink_200_200/profile-displayphoto-shrink_200_200/0/1708036651826?e=1745452800&v=beta&t=eDT_LYA3e3x3XQRs18yFnJ2kjAI1BYSZFYp90_AjWRY"
                alt="Cofounder Three"
                sx={{ width: 200, borderRadius: 2 }}
              />
            </Animated>
          </Grid>
          <Grid item xs={12} md={6}>
            <Animated animation="fade-up" delay={200}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                Andrew Byerly
              </Typography>
              <Typography variant="body1">
                Andrew is the COO and CRO. Andrew's expertise in business
                management and administration is the backbone of our company. He
                ensures that we do not have a leaking bucket and are able to
                grow and scale our business while providing the best service to
                our customers as well as tangible value in form of results.
              </Typography>
            </Animated>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
